import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import axios, { CancelTokenSource } from 'axios'
import { BTN_TXT, PLACEHOLDERS } from 'constants/txt'
import { Button, BUTTON_TYPES } from '../../../components/Button'
import { Input } from '../../../components/Input'
import { Table } from 'components/Table'
import { AddHealthSystemPopup } from './AddHealthSystemPopup/AddHealthSystemPopup'
import { DeleteHealthSystemPopup } from './DeleteHealthSystemPopup/DeleteHealthSystemPopup'
import { useDebounce } from '../../../hooks/hooks'
import { setLoading } from '../../../redux/store/common/slice'
import { useContractCategoryDetailsContext } from '../Providers/ContractCategoryDetailsContextProvider'
import { useContractCategoryDetailsPopup } from '../Providers/ContractCategoryDetailsPopupProvider'
import { getCCHealthSystems } from '../api'
import { isTableHasParams } from 'helper/common'
import { columns } from './constants'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as ArrowDown16 } from 'assets/svg/ArrowDown16.svg'
import { ReactComponent as ArrowRight16 } from 'assets/svg/ArrowRight16.svg'
import {
  THealthSystemTableRequestData,
  THealthSystemUsersParams
} from '../../HealthSystemUsers/types'
import { ICCHealthSystemWithHospitals } from './types'
import './styles.scss'
import { Typography, TYPOGRAPHY_WEIGHT } from '../../../components/Typography'
import cn from 'classnames'
import { LED_BY_VALUE } from '../../../components/LeadByIndicator/constants'
import { EditExpectedSavingsAndSpendPopup } from './EditExpectedSavingsAndSpendPopup/EditExpectedSavingsAndSpendPopup'

export const Facilities = () => {
  const { addHealthSystemPopup } = useContractCategoryDetailsPopup()
  const dispatch = useDispatch()
  const { actions, state } = useContractCategoryDetailsContext()
  const { getCCDetailsAsync } = actions
  const {
    canManageHS,
    details,
    canEditExpectedSpendAndSavings,
    canViewExpectedSpendAndSavings,
    canSeeHealthSystem
  } = state
  const [totalNumber, setTotalNumber] = useState(0)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<
    ICCHealthSystemWithHospitals[]
  >([])
  const [searchValue, setSearchValue] = useState('')
  const [tableData, setTableData] = useState<ICCHealthSystemWithHospitals[]>([])
  const [sortParams, setSortParams] = useState<THealthSystemTableRequestData>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])

  const { id } = useParams()

  const tableDataTokenRef = useRef<CancelTokenSource>()
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const hideSelectHSLevel = useMemo(() => {
    return (
      [
        LED_BY_VALUE.HEALTH_SYSTEM,
        LED_BY_VALUE.LIFELINE_FOR_HEALTH_SYSTEM,
        LED_BY_VALUE.NATIONAL_GPO
      ].includes(details.led_by) || tableData.length < 2
    )
  }, [details.led_by, tableData])

  const selectedRowsHSLevel = useMemo(() => {
    const uniqueHS: string[] = []
    selectedRows
      .filter((selectedRow) => selectedRow.hospital_level)
      .forEach((selectedRow) => {
        if (
          selectedRow.hs_uuid &&
          !uniqueHS.find((hs) => hs === selectedRow.hs_uuid)
        )
          uniqueHS.push(selectedRow.hs_uuid)
      })
    return uniqueHS.length
  }, [selectedRows])

  const debouncedSearch = useDebounce(searchValue, 500)
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => {
        getTableData({ ...sortParams, page: { ...DEFAULT_PAGE } })
        setPageInfo({ ...DEFAULT_PAGE })
      },
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: THealthSystemTableRequestData) => {
    dispatch(setLoading(true))
    if (firstLoad) {
      setFirstLoad(false)
    }
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: THealthSystemUsersParams = { ...filters }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    if (searchValue) {
      params.search = searchValue
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getCCHealthSystems(id as string, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            key: i.uuid,
            hospital_level: false,
            children: i.hospitals.map((hospital) => {
              return {
                ...hospital,
                key: `${hospital.uuid}-hospital`,
                hospital_level: true,
                hs_uuid: i.uuid
              }
            })
          }))
        )
        setSelectedRowKeys([])
        setSelectedRows([])
        setTotalNumber(resp.data.count)
        getCCDetailsAsync()
      })
      .finally(() => dispatch(setLoading(false)))
  }

  useEffect(() => {
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData[0]?.uuid])

  const disableDeleteButton = useMemo(() => {
    return hideSelectHSLevel
      ? tableData.length === 1 &&
          tableData[0]?.children.length ===
            selectedRows.filter((selectedRow) => selectedRow.hospital_level)
              .length
      : tableData.length ===
          selectedRows.filter((selectedRow) => !selectedRow.hospital_level)
            .length
  }, [tableData, selectedRows, hideSelectHSLevel])

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }
  const handleSelectRow = (selectedRowKeys, selectedRow) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRow)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  const updateHSList = () =>
    getTableData({
      ...sortParams
    })

  const tableColumns = useMemo(
    () => columns(canViewExpectedSpendAndSavings, canSeeHealthSystem),
    [canViewExpectedSpendAndSavings, canSeeHealthSystem]
  )

  const displayData = useMemo(
    () =>
      canSeeHealthSystem
        ? tableData
        : (tableData.flatMap(
            (hs) => hs.children
          ) as ICCHealthSystemWithHospitals[]),
    [canSeeHealthSystem, tableData]
  )

  return (
    <div className="table-wrapper">
      <div className="table-wrapper__header">
        {!!selectedRowKeys.length ? (
          <Typography.Body1
            className="table-wrapper__select-title"
            weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
          >
            Selected{' '}
            {canSeeHealthSystem ? selectedRowsHSLevel : selectedRows.length} of{' '}
            {totalNumber}
          </Typography.Body1>
        ) : (
          <Input
            className="allow-clear"
            propsInput={{
              allowClear: { clearIcon: <Close16 /> },
              className: 'rfp-hs-users_search',
              placeholder: PLACEHOLDERS.SEARCH,
              prefix: <Search16 />,
              value: searchValue,
              onChange: (e) => setSearchValue(e.target.value)
            }}
          />
        )}
        <div className="table-wrapper__header_buttons row gap-8">
          {canEditExpectedSpendAndSavings && (
            <EditExpectedSavingsAndSpendPopup
              selectedHS={selectedRows.filter((row) => row.hospital_level)}
              updateHSList={updateHSList}
            />
          )}
          {canManageHS && (
            <>
              {!selectedRowKeys.length && (
                <Button
                  type={BUTTON_TYPES.PRIMARY}
                  icon={<Plus16 />}
                  onClick={addHealthSystemPopup.actions.open}
                  upperCase
                >
                  {canSeeHealthSystem
                    ? BTN_TXT.ADD_SYSTEM
                    : BTN_TXT.ADD_HOSPITAL}
                </Button>
              )}
              <DeleteHealthSystemPopup
                updateHSList={updateHSList}
                selectedHS={selectedRows}
                disableButton={disableDeleteButton}
              />
            </>
          )}
        </div>
      </div>
      <Table<ICCHealthSystemWithHospitals>
        dataSource={displayData}
        columns={tableColumns}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        className={cn('rfp-vendors-data-list-table contacts-hs-list-table', {
          selectable: canManageHS && canSeeHealthSystem
        })}
        onChangePage={handleChangePageSize}
        onChange={handleTableChange}
        hasSearchOrFilters={isTableHasParams(debouncedSearch, false)}
        expandable={
          canSeeHealthSystem
            ? {
                defaultExpandAllRows: false,
                columnWidth: 40,
                expandedRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) setExpandedRowKeys([record.uuid])
                  else setExpandedRowKeys([])
                },
                expandIcon: ({ expanded, onExpand, record, expandable }) =>
                  expandable ? (
                    expanded ? (
                      <div
                        className="expand-wrapper row gap-12 align-center"
                        onClick={(e) => onExpand(record, e)}
                      >
                        <ArrowDown16 />
                      </div>
                    ) : (
                      <div
                        className="expand-wrapper row gap-12 align-center"
                        onClick={(e) => onExpand(record, e)}
                      >
                        <ArrowRight16 />
                      </div>
                    )
                  ) : (
                    <></>
                  )
              }
            : undefined
        }
        createButton={
          canManageHS
            ? {
                buttonText: canSeeHealthSystem
                  ? BTN_TXT.ADD_SYSTEM
                  : BTN_TXT.ADD_HOSPITAL,
                availability: true,
                action: addHealthSystemPopup.actions.open
              }
            : undefined
        }
        rowSelection={
          canManageHS
            ? {
                selectedRowKeys,
                onChange: handleSelectRow,
                hideSelectAll: hideSelectHSLevel,
                checkStrictly: false,
                getCheckboxProps: ({ hospital_level }) => {
                  return {
                    disabled: hideSelectHSLevel ? !hospital_level : false
                  }
                }
              }
            : undefined
        }
      />
      <AddHealthSystemPopup
        updateList={updateHSList}
        canSeeHealthSystem={canSeeHealthSystem}
      />
    </div>
  )
}
